import React, { useEffect, useMemo, useState } from 'react'

import { useAccountActions } from 'redux/actions/Accounts'
import { useCompanyActions } from 'redux/actions/Companies'
import { useCompanyGroupActions } from 'redux/actions/CompanyGroups'

import { ParsedCompanyGroup } from 'types/GlobalCompanyGroups'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useTranslation } from 'language/translator'

import Alert from 'components_new/atoms/Alert'
import Button from 'components_new/atoms/Button'
import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Link from 'components_new/atoms/Link'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'
import List from 'components_new/atoms/List'
import ListItem from 'components_new/atoms/List/ListItem'
import ListItemText from 'components_new/atoms/List/ListItemText'

import AdvancedDialog from 'components_new/molecules/AdvancedDialog'
import SearchAndSelect from 'components_new/molecules/SearchAndSelect'
import SearchField from 'components_new/molecules/SearchField'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import SimpleTable from 'components_new/molecules/SimpleTable'

import CompanyStructureSyncAlert from 'components_new/organisms/CompanyStructureSyncAlert'
import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import EditCompanyGroupDialog from 'components_new/organisms/dialogs/EditCompanyGroupDialog'

import SettingsTemplate from 'components_new/templates/SettingsTemplate'

import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { useAccounts, useRoles } from 'redux/hooks/Accounts'
import { useCompaniesData, useCompaniesFetched } from 'redux/hooks/Companies'
import {
  useCompanyGroupsData,
  useCompanyGroupsFetched
} from 'redux/hooks/CompanyGroups'
import { useCustomer, useIsManagementCompany } from 'redux/hooks/Customer'

const CompaniesPage = () => {
  const companiesData = useCompaniesData()
  const companiesFetched = useCompaniesFetched()
  const companyGroupsData = useCompanyGroupsData()
  const companyGroupsFetched = useCompanyGroupsFetched()

  const { tryGetAllCompanies, tryUpdateCompany } = useCompanyActions()
  const {
    tryAddCompanyToCompanyGroup,
    tryCreateCompanyGroup,
    tryDeleteCompanyGroup,
    tryRemoveCompanyFromCompanyGroup,
    tryUpdateCompanyGroup
  } = useCompanyGroupActions()
  const { tryDeleteUser } = useAccountActions()

  const { isHomepal } = useAccountSettings()
  const { t } = useTranslation()
  const { accounts, loading: accountsLoading } = useAccounts(true)
  const { loading: rolesLoading } = useRoles()

  useEffect(() => {
    if (!companiesFetched) {
      tryGetAllCompanies()
    }
  }, [])

  const [
    showCompaniesWithoutCompanyGroup,
    setShowCompaniesWithoutCompanyGroup
  ] = useState<boolean>(false)

  const [createCompanyGroupOpen, setCreateCompanyGroupOpen] =
    useState<boolean>(false)
  const [isCreating, setIsCreating] = useState<boolean>(false)
  const [newCompanyGroupName, setNewCompanyGroupName] = useState<string>('')
  const [idsToShow, setIdsToShow] = useState<{ [id: string]: boolean }>({})

  const [editCompanyGroupId, setEditCompanyGroupId] = useState<string | null>(
    null
  )

  const [companyGroupToDelete, setCompanyGroupToDelete] =
    useState<ParsedCompanyGroup | null>(null)

  const customer = useCustomer()
  const isManagementCompany = useIsManagementCompany()

  const companies = Object.values(companiesData)
  const companyGroups = Object.values(companyGroupsData)

  const parsedCompanyGroups = companyGroups
    .map((companyGroup) => {
      const filteredCompanies = companies.filter(
        (company) =>
          companyGroup.companies.includes(company.id) && !company.ignore
      )

      return {
        id: companyGroup.id,
        name: companyGroup.name,
        accounts: accounts.filter(
          (acc) => acc.company_group_id === companyGroup.id
        ),
        companies: filteredCompanies
      }
    })
    .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))

  const editCompanyGroup =
    parsedCompanyGroups.find((cg) => cg.id === editCompanyGroupId) || null
  const companiesWithoutCompanyGroup = useMemo(
    () =>
      companies
        .filter(
          (company) =>
            !companyGroups.some((companyGroup) =>
              companyGroup.companies.includes(company.id)
            ) && !company.ignore
        )
        .sort((a, b) => sortAlphabeticalAsc(a.name, b.name)),
    [companies, companyGroups]
  )

  const integrateCompanyGroups = customer?.integrate_company_groups

  const handleSubmit = (value: string[]) => {
    companies.forEach((company) => {
      const selected = value.includes(company.id)
      const ignored = !selected

      if (company.ignore !== ignored) {
        tryUpdateCompany({
          data: { id: company.id, ignore: ignored }
        })
      }
    })
  }

  const [searchQuery, setSearchQuery] = useState('')

  const filteredCompanyGroups = parsedCompanyGroups.filter((companyGroup) =>
    companyGroup.name?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const loading =
    accountsLoading ||
    rolesLoading ||
    !companiesFetched ||
    !companyGroupsFetched

  return (
    <>
      <SettingsTemplate>
        <CompanyStructureSyncAlert />

        {isManagementCompany ? (
          <Box sx={{ mb: 2 }}>
            <SectionHeader
              actions={
                companiesWithoutCompanyGroup.length > 0 ? (
                  <>
                    <strong>
                      <Link
                        color="inherit"
                        // eslint-disable-next-line max-len
                        onClick={() =>
                          setShowCompaniesWithoutCompanyGroup(true)
                        }
                      >
                        {t(
                          'company_groups',
                          '{{count}}_missing_company_group',
                          false,
                          {
                            count:
                              companiesWithoutCompanyGroup.length.toString()
                          }
                        )}
                      </Link>
                    </strong>
                  </>
                ) : null
              }
              gutterBottom={true}
              title={t('global', 'company_groups')}
            />

            {/*-- search --*/}
            <SearchField
              onClear={() => setSearchQuery('')}
              onSearch={(value) => setSearchQuery(value)}
              size="small"
              sx={{ mb: 1 }}
              value={searchQuery}
            />

            {/*-- table --*/}
            <SimpleTable
              addRow={
                integrateCompanyGroups
                  ? undefined
                  : {
                      onClick: () => setCreateCompanyGroupOpen(true)
                    }
              }
              border={true}
              maxHeight={400}
              columns={[
                { title: t('global', 'name'), width: 250 },
                { title: t('global', 'companies') },
                { title: t('global', 'users') },
                { title: t('global', 'id') },
                { align: 'right' }
              ]}
              hover={true}
              loading={loading}
              rows={filteredCompanyGroups.map((companyGroup) => {
                return {
                  cells: [
                    <React.Fragment key={0}>
                      {companyGroup.name}
                    </React.Fragment>,
                    <React.Fragment key={1}>
                      {companyGroup.companies.length}{' '}
                      {t('global', 'companies', true)}
                    </React.Fragment>,
                    <React.Fragment key={2}>
                      {companyGroup.accounts.length}{' '}
                      {t('global', 'users', true)}
                    </React.Fragment>,
                    <React.Fragment key={3}>
                      {idsToShow[companyGroup.id] ? (
                        companyGroup.id
                      ) : (
                        <Link
                          onClick={() =>
                            setIdsToShow({
                              ...idsToShow,
                              [companyGroup.id]: true
                            })
                          }
                        >
                          {t('company_groups', 'show_id')}
                        </Link>
                      )}
                    </React.Fragment>,
                    <React.Fragment key={4}>
                      <IconButton
                        onClick={() => setEditCompanyGroupId(companyGroup.id)}
                        size="small"
                      >
                        <Icon fontSize="small" name="Edit" />
                      </IconButton>
                      {integrateCompanyGroups ? null : (
                        <IconButton
                          edge="end"
                          onClick={() => setCompanyGroupToDelete(companyGroup)}
                          size="small"
                        >
                          <Icon fontSize="small" name="Delete" />
                        </IconButton>
                      )}
                    </React.Fragment>
                  ]
                }
              })}
            />

            <Box sx={{ maxWidth: 'content', mt: 1 }}>
              <Text color="text.secondary" variant="caption">
                {t('company_groups', 'helper_text')}
              </Text>
            </Box>
          </Box>
        ) : null}

        <SectionHeader gutterBottom={true} title={t('global', 'companies')} />

        <SearchAndSelect
          autoFocus={!isManagementCompany}
          loading={loading}
          onSubmit={handleSubmit}
          options={[...companies]
            .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))
            .map((company) => {
              return {
                id: company.id,
                title: company.name
              }
            })}
          size="small"
          value={companies
            .filter((company) => !company.ignore)
            .map((company) => company.id)}
        />

        <Box sx={{ maxWidth: 'content' }}>
          <Text color="text.secondary" variant="caption">
            {t('companies', 'activation_status_helper_text')}
          </Text>
        </Box>
      </SettingsTemplate>

      {/*-- dialogs --*/}
      <EditCompanyGroupDialog
        addCompany={(companyId, callback) =>
          tryAddCompanyToCompanyGroup(
            editCompanyGroupId as string,
            companyId,
            callback
          )
        }
        companiesWithoutCompanyGroup={companiesWithoutCompanyGroup}
        companyGroup={editCompanyGroup}
        deleteUser={tryDeleteUser}
        hasIdp={false}
        integrateCompanyGroups={!!integrateCompanyGroups}
        isHomepal={isHomepal}
        onClose={() => setEditCompanyGroupId(null)}
        open={Boolean(editCompanyGroupId)}
        removeCompany={(companyId, callback) =>
          tryRemoveCompanyFromCompanyGroup(
            editCompanyGroupId as string,
            companyId,
            callback
          )
        }
        updateCompanyGroup={tryUpdateCompanyGroup}
      />

      <DeleteDialog
        onClose={() => setCompanyGroupToDelete(null)}
        onDelete={(callback: () => void) => {
          tryDeleteCompanyGroup(
            (companyGroupToDelete as ParsedCompanyGroup).id,
            (err) => {
              if (!err) {
                setCompanyGroupToDelete(null)
                callback()
              }
            }
          )
        }}
        open={Boolean(companyGroupToDelete)}
        title={companyGroupToDelete?.name}
        type={t('global', 'company_group', true)}
      >
        <Alert severity="info">
          {t('company_groups', 'delete_company_group_warning')}
        </Alert>
      </DeleteDialog>

      <SimpleDialog
        actions={
          <>
            <Button
              disabled={isCreating}
              variant="text"
              onClick={() => {
                setCreateCompanyGroupOpen(false)
                setNewCompanyGroupName('')
              }}
            >
              {t('global', 'cancel')}
            </Button>
            <Button
              loading={isCreating}
              onClick={() => {
                setIsCreating(true)

                tryCreateCompanyGroup(newCompanyGroupName, undefined, () => {
                  setIsCreating(false)
                  setCreateCompanyGroupOpen(false)
                  setNewCompanyGroupName('')
                })
              }}
            >
              {t('global', 'add')}
            </Button>
          </>
        }
        onClose={() => {
          setCreateCompanyGroupOpen(false)
          setNewCompanyGroupName('')
        }}
        open={createCompanyGroupOpen}
        title={t('company_groups', 'add_company_group')}
      >
        <TextField
          label={t('global', 'name')}
          required={true}
          autoComplete="off"
          value={newCompanyGroupName}
          onChange={(event) => setNewCompanyGroupName(event.target.value)}
        />
      </SimpleDialog>

      <AdvancedDialog
        actions={
          <Button
            variant="text"
            onClick={() => setShowCompaniesWithoutCompanyGroup(false)}
          >
            {t('global', 'close')}
          </Button>
        }
        onClose={() => setShowCompaniesWithoutCompanyGroup(false)}
        open={showCompaniesWithoutCompanyGroup}
        title={t('company_groups', '{{count}}_missing_company_group', false, {
          count: companiesWithoutCompanyGroup.length.toString()
        })}
      >
        <List>
          {companiesWithoutCompanyGroup.map((company) => (
            <ListItem
              key={company.id}
              secondaryAction={
                <Button
                  disabled={isCreating}
                  variant="text"
                  onClick={() => {
                    setIsCreating(true)

                    tryCreateCompanyGroup(company.name, company.id, () =>
                      setIsCreating(false)
                    )
                  }}
                >
                  {t('company_groups', 'add_to_new_company_group')}
                </Button>
              }
            >
              <Icon color="disabled" name="ApartmentOutlined" />
              <ListItemText
                sx={{ ml: 3 }}
                primary={company.name}
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </ListItem>
          ))}
        </List>
      </AdvancedDialog>
    </>
  )
}

export default CompaniesPage
